import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { formatCurrency, formatDate } from '../../../lib/formatters.js';

interface RecentClaim {
  id: string;
  claimNumber: string;
  claimType: string;
  claimedAmount: string;
  isoCurrencyCode: string;
  status: string;
  submittedAt: string | null;
}

interface SimilarClaim {
  id: string;
  claimNumber: string;
  claimedAmount: string;
  approvedAmount: string | null;
  isoCurrencyCode: string;
}

interface SchemeHistory {
  totalCount: number;
  totalApproved: string | null;
  avgApprovalRate: string | null;
}

interface AnomalyFlag {
  flagType: string;
  severity: string;
  description: string;
}

interface CrossReferences {
  recentFromDistributor: RecentClaim[];
  similarApproved: SimilarClaim[];
  schemeHistory: SchemeHistory | null;
  anomalyFlags: AnomalyFlag[];
}

interface CrossReferencePanelProps {
  claimId: string;
}

const STATUS_COLORS: Record<string, string> = {
  APPROVED: 'bg-success/10 text-success',
  AUTO_APPROVED: 'bg-success/10 text-success',
  REJECTED: 'bg-danger/10 text-danger',
  AWAITING_REVIEW: 'bg-copper-soft text-copper-deep',
  SUBMITTED: 'bg-copper-soft text-copper-deep',
  DRAFT: 'bg-slate-100 text-slate-600',
  PAID: 'bg-success/10 text-success',
};

const SEVERITY_COLORS: Record<string, string> = {
  HIGH: 'text-danger',
  MEDIUM: 'text-copper',
  LOW: 'text-slate-500',
};

export function CrossReferencePanel({ claimId }: CrossReferencePanelProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<CrossReferences | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<CrossReferences>(`/approver/claims/${claimId}/cross-references`)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [claimId]);

  if (loading) {
    return (
      <div className="space-y-2 animate-pulse">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-10 bg-slate-100 rounded" />
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <p className="text-sm text-slate-400 py-4 text-center">{t('errors.internalServerError')}</p>
    );
  }

  const anomalyCount = data.anomalyFlags.length;

  return (
    <div className="space-y-2">
      {/* Recent claims from distributor */}
      <details className="group border border-hairline rounded overflow-hidden">
        <summary className="cursor-pointer list-none px-3 py-2.5 bg-bone hover:bg-bone-soft transition-colors flex items-center justify-between select-none">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('approver.crossRef.recentFromDistributor', {
              defaultValue: 'Recent claims from this distributor',
            })}
          </span>
          <span className="text-xs text-slate-400 group-open:hidden">
            ({data.recentFromDistributor.length})
          </span>
        </summary>
        <div className="px-3 pb-3 pt-2">
          {data.recentFromDistributor.length === 0 ? (
            <p className="text-xs text-slate-400 py-1">
              {t('approver.crossRef.none', { defaultValue: 'None found.' })}
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-hairline">
                  <th className="text-left pb-1 pr-2 font-medium">
                    {t('claims.claimNumber')}
                  </th>
                  <th className="text-left pb-1 pr-2 font-medium">
                    {t('claims.type')}
                  </th>
                  <th className="text-right pb-1 pr-2 font-medium">
                    {t('claims.amount')}
                  </th>
                  <th className="text-left pb-1 font-medium">
                    {t('claims.status')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {data.recentFromDistributor.map((rc) => (
                  <tr key={rc.id}>
                    <td className="py-1.5 pr-2 font-mono text-slate-600">{rc.claimNumber}</td>
                    <td className="py-1.5 pr-2 text-slate-500">
                      {t(`wizard.claimTypes.${rc.claimType}.label`, {
                        defaultValue: rc.claimType,
                      })}
                    </td>
                    <td className="py-1.5 pr-2 text-right font-mono">
                      {formatCurrency(Number(rc.claimedAmount), rc.isoCurrencyCode)}
                    </td>
                    <td className="py-1.5">
                      <span
                        className={[
                          'px-1.5 py-0.5 rounded-sm font-semibold',
                          STATUS_COLORS[rc.status] ?? 'bg-slate-100 text-slate-500',
                        ].join(' ')}
                      >
                        {t(`wizard.status.${rc.status}`, { defaultValue: rc.status })}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </details>

      {/* Similar approved claims */}
      <details className="group border border-hairline rounded overflow-hidden">
        <summary className="cursor-pointer list-none px-3 py-2.5 bg-bone hover:bg-bone-soft transition-colors flex items-center justify-between select-none">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('approver.crossRef.similarApproved', {
              defaultValue: 'Similar approved claims (30d)',
            })}
          </span>
          <span className="text-xs text-slate-400 group-open:hidden">
            ({data.similarApproved.length})
          </span>
        </summary>
        <div className="px-3 pb-3 pt-2">
          {data.similarApproved.length === 0 ? (
            <p className="text-xs text-slate-400 py-1">
              {t('approver.crossRef.none', { defaultValue: 'None found.' })}
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-hairline">
                  <th className="text-left pb-1 pr-2 font-medium">{t('claims.claimNumber')}</th>
                  <th className="text-right pb-1 pr-2 font-medium">
                    {t('approver.crossRef.claimed', { defaultValue: 'Claimed' })}
                  </th>
                  <th className="text-right pb-1 font-medium">
                    {t('approver.crossRef.approved', { defaultValue: 'Approved' })}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {data.similarApproved.map((sc) => (
                  <tr key={sc.id}>
                    <td className="py-1.5 pr-2 font-mono text-slate-600">{sc.claimNumber}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">
                      {formatCurrency(Number(sc.claimedAmount), sc.isoCurrencyCode)}
                    </td>
                    <td className="py-1.5 text-right font-mono">
                      {sc.approvedAmount != null
                        ? formatCurrency(Number(sc.approvedAmount), sc.isoCurrencyCode)
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </details>

      {/* Scheme history */}
      <details className="group border border-hairline rounded overflow-hidden">
        <summary className="cursor-pointer list-none px-3 py-2.5 bg-bone hover:bg-bone-soft transition-colors select-none">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {data.schemeHistory == null
              ? t('approver.crossRef.noSchemeLinked', { defaultValue: 'No scheme linked' })
              : t('approver.crossRef.schemeHistory', { defaultValue: "This scheme's history" })}
          </span>
        </summary>
        <div className="px-3 pb-3 pt-2">
          {data.schemeHistory == null ? (
            <p className="text-xs text-slate-400 py-1">
              {t('approver.crossRef.noSchemeLinked', { defaultValue: 'No scheme linked' })}
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="font-serif italic text-2xl text-copper">
                  {data.schemeHistory.totalCount}
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mt-0.5">
                  {t('approver.crossRef.totalClaims', { defaultValue: 'Total Claims' })}
                </div>
              </div>
              <div>
                <div className="font-mono text-sm text-ink font-semibold">
                  {data.schemeHistory.totalApproved != null
                    ? formatCurrency(Number(data.schemeHistory.totalApproved), 'IDR')
                    : '—'}
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mt-0.5">
                  {t('approver.crossRef.totalApproved', { defaultValue: 'Total Approved' })}
                </div>
              </div>
              <div>
                <div className="font-mono text-sm text-ink font-semibold">
                  {data.schemeHistory.avgApprovalRate != null
                    ? `${Math.round(Number(data.schemeHistory.avgApprovalRate) * 100)}%`
                    : '—'}
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mt-0.5">
                  {t('approver.crossRef.avgApprovalRate', { defaultValue: 'Avg Approval Rate' })}
                </div>
              </div>
            </div>
          )}
        </div>
      </details>

      {/* Anomaly flags */}
      <details
        className={[
          'group border rounded overflow-hidden',
          anomalyCount > 0 ? 'border-danger/30' : 'border-hairline',
        ].join(' ')}
      >
        <summary className="cursor-pointer list-none px-3 py-2.5 bg-bone hover:bg-bone-soft transition-colors flex items-center justify-between select-none">
          <span
            className={[
              'text-xs font-semibold uppercase tracking-wide',
              anomalyCount > 0 ? 'text-danger' : 'text-slate-500',
            ].join(' ')}
          >
            {t('approver.crossRef.anomalyFlags', { defaultValue: 'Anomaly flags' })}
          </span>
          {anomalyCount > 0 && (
            <span className="text-xs bg-danger/10 text-danger px-1.5 py-0.5 rounded-sm font-semibold">
              {anomalyCount}
            </span>
          )}
        </summary>
        <div className="px-3 pb-3 pt-2">
          {data.anomalyFlags.length === 0 ? (
            <p className="text-xs text-success py-1">
              ✓ {t('approver.crossRef.noAnomalies', { defaultValue: 'No anomalies detected.' })}
            </p>
          ) : (
            <div className="space-y-2">
              {data.anomalyFlags.map((flag, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span
                    className={[
                      'text-xs font-bold shrink-0 mt-0.5',
                      SEVERITY_COLORS[flag.severity] ?? 'text-slate-500',
                    ].join(' ')}
                  >
                    ⚠
                  </span>
                  <div>
                    <p className="text-xs font-medium text-ink">
                      {t(`approver.anomaly.${flag.flagType}`, { defaultValue: flag.flagType })}
                      <span
                        className={[
                          'ml-1 text-xs',
                          SEVERITY_COLORS[flag.severity] ?? 'text-slate-400',
                        ].join(' ')}
                      >
                        ({flag.severity})
                      </span>
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{flag.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </details>

      {/* Timestamp note */}
      <p className="text-xs text-slate-400 italic px-1">
        {t('approver.crossRef.note', {
          defaultValue: 'Cross-reference data reflects the last 90 days.',
        })}
      </p>
    </div>
  );
}

// Suppress unused import warning — formatDate imported for potential future use
void formatDate;
